// PHOTO ACTIONS for the ribbon host (R2, 2026-07-20) — the old toolbar's
// photo machinery, lifted whole when the image branch moved up into Row 1's
// image morph (imageRow1). One hook owns everything a photo verb needs
// beyond the editor facade:
//   · the Cropper.js modal (openCropModal / apply / cancel) + the
//     dblclick-to-crop canvas seam (with the text3d/emboss recipe-image
//     guard — those dblclicks open their OWN edit modals);
//   · the hidden file input behind Replace;
//   · the one-click AI background removal (temp R2 upload → enhance
//     pipeline → swap in place), with its busy flag for the button label.
// The mount wires these to the image.* handler keys; the returned `node`
// renders the modal portal + file input alongside the ribbon.
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Point, util } from 'fabric';
import { useAuth } from '../../../../contexts/AuthContext';
import { useToast } from '../../../../contexts/ToastContext';
import { ImageCropModal } from '../cropModal';
import platform from '../webPlatform';

export default function usePhotoActions(editor) {
  // ---- crop modal (Cropper.js) ----
  const [cropState, setCropState] = useState(null); // { obj, src, initialData } | null
  const { user } = useAuth();
  const toast = useToast();

  // ---- one-click background removal (existing enhance pipeline:
  // temp R2 upload → /wallet/enhance-image remove_bg → swap in place) ----
  const [removeBgBusy, setRemoveBgBusy] = useState(false);
  const removeBg = async () => {
    const canvas = editor?.canvas;
    const obj = canvas?.getActiveObject();
    if (!obj || obj.type !== 'image' || removeBgBusy) return;
    if (!user?.token) {
      toast.showError('Please sign in to remove backgrounds.');
      return;
    }
    setRemoveBgBusy(true);
    try {
      // 1. Full ORIGINAL source → PNG data URL (crop stays non-destructive)
      const el = obj._originalElement || obj._element;
      const w = el?.naturalWidth || el?.width;
      const h = el?.naturalHeight || el?.height;
      if (!w || !h) throw new Error('Image source unavailable');
      const c = document.createElement('canvas');
      c.width = w;
      c.height = h;
      c.getContext('2d').drawImage(el, 0, 0);
      const dataUrl = c.toDataURL('image/png');

      // 2–4. Brightivity: one call to brightivity.dev/design/remove-bg (temp copy, fal-ai/birefnet,
      // temp deleted) returns the cut-out, embedded as a data URL (no remote URL in the design).
      const res = await platform.removeBackground({ dataUrl });
      if (!res?.success || !res.dataUrl) throw new Error(res?.message || res?.error || 'Background removal failed');
      const resultUrl = res.dataUrl;

      // 5. Swap in place: keep the on-screen footprint, reset the crop
      // window to the new source (dimensions may differ)
      const displayW = obj.getScaledWidth();
      // v6: setSrc is promise-based (v5 wrapper hung Remove-BG forever).
      await obj.setSrc(resultUrl, { crossOrigin: 'anonymous' });
      const newEl = obj._originalElement || obj._element;
      obj.set({
        cropX: 0,
        cropY: 0,
        width: newEl?.naturalWidth || obj.width,
        height: newEl?.naturalHeight || obj.height,
      });
      obj.scaleToWidth(displayW);
      obj.setCoords();
      canvas.requestRenderAll();
      canvas.fire('object:modified', { target: obj }); // ONE undo entry
      toast.showSuccess('Background removed!');
    } catch (err) {
      console.error('Remove background failed:', err);
      toast.showError(`Background removal failed: ${err.message || 'try again'}`);
    } finally {
      setRemoveBgBusy(false);
    }
  };

  // ---- replace image in place (keeps footprint/frame; crop resets) ----
  const replaceInputRef = useRef(null);
  const handleReplaceFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = (ev) => editor?.replaceActiveImage(ev.target?.result);
    reader.readAsDataURL(file);
  };

  const openCropModal = () => {
    if (!editor) return;
    const canvas = editor.canvas;
    const obj = canvas.getActiveObject();
    if (!obj || obj.type !== 'image') return;
    const src =
      (typeof obj.getSrc === 'function' && obj.getSrc()) ||
      obj._element?.currentSrc ||
      obj._element?.src;
    if (!src) return;
    const el = obj._originalElement || obj._element;
    const natW = el?.naturalWidth || el?.width || obj.width;
    const natH = el?.naturalHeight || el?.height || obj.height;
    let initialData = null;
    const isCropped =
      (obj.cropX || 0) > 0 || (obj.cropY || 0) > 0 || obj.width < natW || obj.height < natH;
    if (isCropped) {
      initialData = {
        x: Math.round(obj.cropX || 0),
        y: Math.round(obj.cropY || 0),
        width: Math.round(obj.width),
        height: Math.round(obj.height),
      };
    }
    setCropState({ obj, src, initialData });
  };

  const closeCropModal = () => setCropState(null);

  const applyCropFromModal = (data) => {
    const canvas = editor?.canvas;
    const obj = cropState?.obj;
    if (!canvas || !obj || !data) {
      setCropState(null);
      return;
    }
    // Native crop (cropX/cropY/width/height) — a clipPath only masks; native
    // crop shrinks the object's box/print footprint. See 2026-06-08 changelog.
    const el = obj._originalElement || obj._element;
    const natW = el?.naturalWidth || el?.width || obj.width;
    const natH = el?.naturalHeight || el?.height || obj.height;
    const w = Math.max(1, Math.min(Math.round(data.width), natW));
    const h = Math.max(1, Math.min(Math.round(data.height), natH));
    const x = Math.max(0, Math.min(Math.round(data.x), natW - w));
    const y = Math.max(0, Math.min(Math.round(data.y), natH - h));

    const W0 = obj.width;
    const H0 = obj.height;
    const cx0 = obj.cropX || 0;
    const cy0 = obj.cropY || 0;

    if (x === cx0 && y === cy0 && w === W0 && h === H0 && !obj.clipPath) {
      setCropState(null);
      return;
    }

    // Anchor the kept pixels in place via the full transform matrix
    const localCenter = new Point(
      x + w / 2 - cx0 - W0 / 2,
      y + h / 2 - cy0 - H0 / 2,
    );
    const canvasCenter = util.transformPoint(localCenter, obj.calcTransformMatrix());

    obj.set({ cropX: x, cropY: y, width: w, height: h, clipPath: null });
    obj.setPositionByOrigin(canvasCenter, 'center', 'center');
    obj.setCoords();
    canvas.requestRenderAll();
    canvas.fire('object:modified', { target: obj }); // records in undo history
    setCropState(null);
  };

  // Double-click a photo to open the crop modal (Canva-style)
  const openCropModalRef = useRef(openCropModal);
  openCropModalRef.current = openCropModal;
  useEffect(() => {
    const canvas = editor?.canvas;
    if (!canvas) return undefined;
    const onDblClick = (opt) => {
      const t = opt.target;
      if (!t || t.type !== 'image' || t.isIcon || t.name === 'clip') return;
      // Baked recipe text (3D or emboss) is a recipe image — dblclick opens its
      // OWN edit modal (StudioCoreEditor seam), never the cropper (a crop would
      // be blown away by the next re-bake anyway).
      if (t.data?.tool === 'text3d' || t.data?.tool === 'emboss-text') return;
      canvas.setActiveObject(t);
      canvas.requestRenderAll();
      openCropModalRef.current();
    };
    canvas.on('mouse:dblclick', onDblClick);
    return () => canvas.off('mouse:dblclick', onDblClick);
  }, [editor?.canvas]);

  const node = (
    <>
      {cropState && createPortal(
        <ImageCropModal
          src={cropState.src}
          initialData={cropState.initialData}
          onApply={applyCropFromModal}
          onCancel={closeCropModal}
        />,
        document.body,
      )}
      <input
        ref={replaceInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleReplaceFile}
      />
    </>
  );

  return {
    node,
    removeBgBusy,
    openCropModal,
    openReplacePicker: () => replaceInputRef.current?.click(),
    removeBg,
  };
}
